import {
  Dropdown,
  DropdownOption,
} from "components/designV2/dropdown/dropdown";
import { InputField } from "components/designV2/inputfield/inputfield";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { useTranslation } from "react-i18next";
import { CompanyBankAccountType } from "model/Company";
import {
  PassSettingControl,
  PassSettingControls,
} from "./passSettingsV2.styled";
import {
  SystemSettingsInfoBox,
  SystemSettingsInfoContent,
  SystemSettingsInfoText,
  SystemSettingsSectionDescription,
  SystemSettingsSectionHeader,
  SystemSettingsSectionTitle,
  SystemSettingsSectionTitleRow,
} from "./companySystemSettings.styled";
import { SystemSettingRow } from "./systemSettingRow.component";

interface CompanyBankAccountSettingProps {
  agreementId: string;
  accountType: CompanyBankAccountType;
  accountValue: string;
  onChangeAgreementId: (value: string) => void;
  onChangeAccountType: (value: CompanyBankAccountType) => void;
  onChangeAccountValue: (value: string) => void;
}

export const CompanyBankAccountSetting = ({
  agreementId,
  accountType,
  accountValue,
  onChangeAgreementId,
  onChangeAccountType,
  onChangeAccountValue,
}: CompanyBankAccountSettingProps): JSX.Element => {
  const { t } = useTranslation();
  const accountValueDescriptionKey =
    accountType === "InternationalBankAccount"
      ? "System.CompanyBankAccountValueInfoInternationalBankAccount"
      : "System.CompanyBankAccountValueInfoBankAccount";

  const accountTypeOptions: DropdownOption[] = [
    {
      label: t("System.BankAccountTypeOptionBankAccount"),
      value: "BankAccount",
    },
    {
      label: t("System.BankAccountTypeOptionInternationalBankAccount"),
      value: "InternationalBankAccount",
    },
  ];

  const handleAccountTypeChange = (value: string): void => {
    onChangeAccountType(
      value === "InternationalBankAccount"
        ? "InternationalBankAccount"
        : "BankAccount",
    );
  };

  return (
    <>
      <SystemSettingsSectionHeader>
        <SystemSettingsSectionTitleRow>
          <SystemSettingsSectionTitle>
            {t("System.BankSettingsTitle")}
          </SystemSettingsSectionTitle>
        </SystemSettingsSectionTitleRow>
        <SystemSettingsSectionDescription>
          {t("System.BankSettingsDescription")}
        </SystemSettingsSectionDescription>
      </SystemSettingsSectionHeader>

      <SystemSettingRow
        title={t("System.CompanyBankAgreementId")}
        description={t("System.CompanyBankAgreementIdInfo")}
      >
        <PassSettingControls>
          <PassSettingControl>
            <InputField
              ariaLabel={t("System.CompanyBankAgreementId")}
              value={agreementId}
              onChange={onChangeAgreementId}
            />
          </PassSettingControl>
        </PassSettingControls>
      </SystemSettingRow>

      <SystemSettingRow
        title={t("System.CompanyBankAccountType")}
        description={t("System.CompanyBankAccountTypeInfo")}
        controlVerticalAlign="center"
      >
        <PassSettingControls>
          <PassSettingControl>
            <Dropdown
              ariaLabel={t("System.CompanyBankAccountType")}
              options={accountTypeOptions}
              value={accountType}
              onChange={handleAccountTypeChange}
            />
          </PassSettingControl>
        </PassSettingControls>
      </SystemSettingRow>

      <SystemSettingRow
        title={t("System.CompanyBankAccountValue")}
        description={t(accountValueDescriptionKey)}
      >
        <PassSettingControls>
          <PassSettingControl>
            <InputField
              ariaLabel={t("System.CompanyBankAccountValue")}
              value={accountValue}
              onChange={onChangeAccountValue}
            />
          </PassSettingControl>
        </PassSettingControls>
      </SystemSettingRow>

      <SystemSettingsInfoBox>
        <CustomIcon name="v2/info" size="20px" color={ColorV2.Blue4} />
        <SystemSettingsInfoContent>
          <SystemSettingsInfoText>
            {t("System.BankSettingsInfo")}
          </SystemSettingsInfoText>
        </SystemSettingsInfoContent>
      </SystemSettingsInfoBox>
    </>
  );
};
